import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  requestClearFormTemplate,
  requestFormTemplateDelete,
} from '@atom/actions/formTemplateActions';
import DeleteModal from '@atom/components/common/DeleteModal';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Button, Icon, IconButton, Modal } from '@atom/mui';
import {
  ArrowBackIcon,
  DeleteIcon,
  EditIcon,
  RemoveRedEyeIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { FormTemplateType } from '@atom/types/form';
import { FormTemplateDetailState } from '@atom/types/store';

import EditFormModal from './EditFormModal';

import classes from './formBuilder.module.css';

const styles = {
  button: {
    color: colors.brand.blue,
    backgroundColor: colors.neutral.white,
  },
};

interface Props {
  formTemplate: FormTemplateType;
  publishForm: () => void;
  saveFormTemplate: (data: FormTemplateDetailState) => void;
}

const FormBuilderHeader = ({
  formTemplate,
  publishForm,
  saveFormTemplate,
}: Props) => {
  const dispatch = useDispatch();
  const [, { backDestination, navigate }] = useNavigation();

  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [publishOpen, setPublishOpen] = useState<boolean>(false);

  const navigateToFormRepository = () => {
    dispatch(requestClearFormTemplate());
    saveFormTemplate(formTemplate);
    navigate(backDestination('/form-repository'));
  };

  const navigateToFormPreview = () => {
    saveFormTemplate(formTemplate);
    navigate(`/form-builder/${formTemplate.id}/preview`);
  };

  const removeFormTemplate = () => {
    dispatch(requestFormTemplateDelete(formTemplate.id));
    setDeleteOpen(false);
    navigate('/form-repository');
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <div className={classes.nameContainer}>
          <IconButton onClick={navigateToFormRepository} tooltip="Go back">
            <Icon color={colors.neutral.white} icon={ArrowBackIcon} />
          </IconButton>
          <div>{formTemplate.name}</div>
        </div>
        <div className={classes.formBuilderHeader}>
          <IconButton onClick={() => setEditOpen(true)} tooltip="Edit">
            <Icon color={colors.neutral.white} icon={EditIcon} />
          </IconButton>
          <IconButton onClick={navigateToFormPreview} tooltip="Preview">
            <Icon color={colors.neutral.white} icon={RemoveRedEyeIcon} />
          </IconButton>
          <IconButton onClick={() => setDeleteOpen(true)} tooltip="Delete">
            <Icon color={colors.neutral.white} icon={DeleteIcon} />
          </IconButton>
          <Button
            variant="contained"
            style={styles.button}
            onClick={() => setPublishOpen(true)}
          >
            Publish
          </Button>
        </div>
      </div>
      <EditFormModal open={editOpen} onClose={() => setEditOpen(false)} />
      <DeleteModal
        open={deleteOpen}
        title={`Delete ${formTemplate.name}`}
        content="Are you sure you want to delete this form template?"
        onCancel={() => setDeleteOpen(false)}
        onConfirm={removeFormTemplate}
      />
      <Modal
        open={publishOpen}
        cancelButtonText="Cancel"
        confirmButtonText="Publish"
        onCancel={() => setPublishOpen(false)}
        onConfirm={publishForm}
        title="Publish Form"
      >
        Published forms cannot be edited. Are you sure you want to publish this
        form?
      </Modal>
    </>
  );
};

export default FormBuilderHeader;
